import { UserRole } from '@/store/modules/user/profile/types';
import Image, { ImageJSON } from '@/store/models/image';
import { JSONModel, toJSONContext } from '@/store/models/jsonModel';
import { getEnumValue } from '@/utils/enum';
import PracticeRelationship, { PracticeRelationshipJSON } from '@/store/modules/practices/practice-relationship';
import Country, { CountryJSON } from '@/store/models/country';
import i18next from '@/i18n';

export enum UserStatus {
  Inactive = 0,
  Active = 1,
  WaitingActivation = 2,
  PendingInvitation = 3,
}

export const UserStatuses = {
  [UserStatus.Inactive]: i18next.t('Inactive'),
  [UserStatus.Active]: i18next.t('Active'),
  [UserStatus.WaitingActivation]: i18next.t('Waiting Activation'),
  [UserStatus.PendingInvitation]: i18next.t('Pending Invitation'),
};

/** Region row returned on user/profile payloads (`entity_url` from API). */
export interface UserRegionJSON {
  id: number;
  name: string;
  entity_url?: string;
}

export interface UserRegionAssignment {
  id: number;
  name: string;
  entityUrl?: string;
}

/**
 * Normalize API `regions` (objects) and/or legacy `region_ids` / numeric `regions` arrays.
 */
export function parseUserRegionsFromJson(json: {
  regions?: unknown;
  region_ids?: unknown;
}): UserRegionAssignment[] {
  const out: UserRegionAssignment[] = [];
  const pushEntry = (item: unknown) => {
    if (item !== null && typeof item === 'object' && 'id' in (item as object)) {
      const o = item as { id: unknown; name?: unknown; entity_url?: unknown };
      const id = Number(o.id);
      if (!Number.isFinite(id)) {
        return;
      }
      out.push({
        id,
        name: o.name != null ? String(o.name) : '',
        entityUrl: o.entity_url != null ? String(o.entity_url) : undefined,
      });
      return;
    }
    const id = Number(item);
    if (Number.isFinite(id)) {
      out.push({ id, name: '' });
    }
  };

  if (json.regions && Array.isArray(json.regions) && json.regions.length) {
    json.regions.forEach(pushEntry);
  } else if (json.region_ids && Array.isArray(json.region_ids) && json.region_ids.length) {
    json.region_ids.forEach(pushEntry);
  }

  return out;
}

/**
 * Backend: `regions` — array of `{ id, name, entity_url? }` for distributors.
 * Legacy responses may still send `region_ids` (handled in `parseUserRegionsFromJson`).
 */
export interface UserJSON {
  id: number;
  status: number;
  name: string;
  email: string;
  first_name: string;
  last_name: string;
  login: string;
  created_at: string;
  updated_at: string;
  login_count: number;
  role: string;
  actual_role?: string;
  practice: PracticeRelationshipJSON | null;
  image: ImageJSON | ImageJSON[] | null;
  password?: string;
  countries: CountryJSON[];
  regions?: UserRegionJSON[] | number[];
  /** Legacy responses only; prefer `regions`. */
  region_ids?: number[];
  login_monthly_count: number;
  language_code: string;
  /** Organization when `role` is distributor (`users.distributor_id`). */
  distributor_id?: number | null;
}

export default class User extends JSONModel<UserJSON> {
  public id: number = 0;
  public status: UserStatus = UserStatus.Active;
  public name: string = '';
  public email: string = '';
  public firstName: string = '';
  public lastName: string = '';
  public login: string = '';
  public createdAt: string = '';
  public updatedAt: string = '';
  public loginCount: number = 0;
  public role: UserRole = UserRole.User;
  public actualRole?: UserRole;
  public practice: PracticeRelationship = new PracticeRelationship();
  public image: Image | null = null;
  public password: string = '';
  public countries: Country[] = [];
  /** Distributor regions from API / editor; serialized as `regions` on save. */
  public regions: UserRegionAssignment[] = [];
  public loginMonthlyCount: number = 0;
  public languageCode: string = 'en';
  /** Set when the user is a distributor; maps to `distributor_id` on save. */
  public distributorId: number | null = null;

  get fullName(): string {
    const name = [this.firstName, this.lastName].filter((val) => Boolean(val)).join(' ');

    if (name) {
      return name;
    }

    if (this.email) {
      return this.email;
    }

    return this.name;
  }

  get practiceName(): string {
    return this.practice && this.practice.title ? this.practice.title : '';
  }

  public get avatar() {
    if (this.image && this.image.thumbnail) {
      return this.image.thumbnail + '?fit=facearea&facepad=3&w=150&h=150';
    }

    return require('@/assets/images/profile-empty-150.png');
  }

  public fromJSON(json: Partial<UserJSON>) {
    if (json.id) {
      this.id = Number(json.id);
    }

    const status = getEnumValue<UserStatus>(UserStatus, json.status);
    if (typeof status !== 'undefined') {
      this.status = status;
    }

    if (json.name) {
      this.name = String(json.name);
    }

    if (json.email) {
      this.email = String(json.email);
    }

    if (json.first_name) {
      this.firstName = String(json.first_name);
    }

    if (json.last_name) {
      this.lastName = String(json.last_name);
    }

    if (json.login) {
      this.login = String(json.login);
    }

    if (json.created_at) {
      this.createdAt = String(json.created_at);
    }

    if (json.updated_at) {
      this.updatedAt = String(json.updated_at);
    }

    if (json.login_count) {
      this.loginCount = Number(json.login_count);
    }

    const role = getEnumValue<UserRole>(UserRole, json.role);
    if (role) {
      this.role = role;
    }

    const actualRole = getEnumValue<UserRole>(UserRole, json.actual_role);
    if (actualRole) {
      this.actualRole = actualRole;
    }

    if (json.practice) {
      this.practice = PracticeRelationship.fromJSON(json.practice);
    }

    if (json.image) {
      this.image = Image.fromJSON(Array.isArray(json.image) ? json.image[0] : json.image);
    }

    if (json.countries && json.countries.length) {
      this.countries = json.countries.map((item) => Country.fromJSON(item));
    }

    this.regions = parseUserRegionsFromJson({
      regions: json.regions,
      region_ids: json.region_ids,
    });

    if (json.login_monthly_count) {
      this.loginMonthlyCount = Number(json.login_monthly_count);
    }

    if (json.language_code) {
      this.languageCode = String(json.language_code);
    }

    if (json.distributor_id !== undefined && json.distributor_id !== null) {
      const did = Number(json.distributor_id);
      this.distributorId = Number.isFinite(did) && did > 0 ? did : null;
    } else {
      this.distributorId = null;
    }
  }

  public toJSON(context: toJSONContext = 'server') {
    const json = super.toJSON(context) as UserJSON;

    // Remove password if not defined
    if (!json.password || !json.password.length) {
      json.password = undefined;
    }

    if (this.actualRole) {
      json.role = this.actualRole;
    }

    if (context === 'server') {
      const effectiveRole = this.actualRole ?? this.role;
      if (effectiveRole === UserRole.Distributor) {
        json.countries = [];
        if (this.distributorId != null && this.distributorId > 0) {
          json.distributor_id = this.distributorId;
        } else {
          delete json.distributor_id;
        }
      } else {
        delete json.distributor_id;
      }
    }

    return json;
  }
}
